/**
 * 刷新账本：记录"刷过几次、为哪个版本刷的、什么时候刷的"，用来阻止刷新死循环。
 *
 * 为什么需要：发布期间 `replicas: 1` + 默认 RollingUpdate 会让新旧两个 pod 同时存活
 * （maxSurge=1, maxUnavailable=0），请求随机分发。于是可能出现
 * "页面是 A → 探测命中新 pod 看到 B → reload → 落到旧 pod，页面又变回 A"的来回横跳。
 * 没有账本，这种窗口会让浏览器无限 reload。
 *
 * 为什么用 sessionStorage 而不是 localStorage：
 * - 新开一个标签页应该是全新的机会（大屏被手动重开时不该继承退避状态）；
 * - logout 时 Persistent.clearAll 会清 localStorage，账本不该被牵连。
 */

import type { AutoRefreshSetting } from '/@/settings/autoRefreshSetting';

/** 版本刷新账本 */
const RELOAD_KEY = 'AR__RELOAD__';
/** chunk 加载失败救场账本，与版本刷新分开计数，避免互相污染 */
const CHUNK_KEY = 'AR__CHUNK__';

interface LedgerState {
  /** 当前窗口内的刷新次数 */
  attempts: number;
  /** 统计窗口起点 */
  windowStart: number;
  /** 上一次为之刷新的目标版本（chunk 账本不关心） */
  lastTarget: string;
  /** 上一次刷新时刻 */
  lastTs: number;
  /** 退避截止时刻，> now 表示正处于退避中 */
  backoffUntil: number;
}

export interface ReloadGate {
  allowed: boolean;
  /** 拒绝原因，用于日志 */
  reason: string;
  /** 是否属于"该退避"的拒绝（触发后应把轮询降频，而不是继续每秒重试） */
  backoff: boolean;
}

const EMPTY: LedgerState = {
  attempts: 0,
  windowStart: 0,
  lastTarget: '',
  lastTs: 0,
  backoffUntil: 0,
};

function readState(key: string): LedgerState {
  try {
    const raw = window.sessionStorage?.getItem(key);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...EMPTY };
    return { ...EMPTY, ...parsed };
  } catch {
    // 隐私模式/存储被禁用：退化为"不记账"，功能仍可用，只是失去防循环能力
    return { ...EMPTY };
  }
}

function writeState(key: string, state: LedgerState): void {
  try {
    window.sessionStorage?.setItem(key, JSON.stringify(state));
  } catch {
    /* 忽略 */
  }
}

/** 窗口过期则清零计数，得到"当前窗口内的有效状态" */
function normalize(state: LedgerState, cfg: AutoRefreshSetting, now: number): LedgerState {
  if (!state.windowStart || now - state.windowStart > cfg.reloadWindowMs) {
    return { ...state, attempts: 0, windowStart: now };
  }
  return state;
}

/**
 * 判定"现在能不能为这个目标版本刷新"。
 * 三条闸：冷却 → 同目标次数 → 窗口内总量。
 */
export function evaluateReloadPermission(
  target: string,
  cfg: AutoRefreshSetting,
  now: number,
): ReloadGate {
  const state = normalize(readState(RELOAD_KEY), cfg, now);

  if (state.lastTs && now - state.lastTs < cfg.reloadCooldownMs) {
    return { allowed: false, reason: 'cooldown', backoff: false };
  }
  // 同一个目标版本刷过 2 次仍没生效，说明不是浏览器的问题（多半是中间层缓存或 pod 横跳），
  // 再刷只会加重问题，转入退避等待窗口自然消失。
  if (state.lastTarget && state.lastTarget === target && state.attempts >= 2) {
    return { allowed: false, reason: `same-target(${target})`, backoff: true };
  }
  if (state.attempts >= cfg.maxReloadPerWindow) {
    return { allowed: false, reason: 'window-limit', backoff: true };
  }
  return { allowed: true, reason: 'ok', backoff: false };
}

/** 记录一次刷新。必须在 reload 之前调用（sessionStorage 在 reload 后仍可读） */
export function recordReloadAttempt(target: string, cfg: AutoRefreshSetting, now: number): void {
  const state = normalize(readState(RELOAD_KEY), cfg, now);
  writeState(RELOAD_KEY, {
    ...state,
    attempts: state.attempts + 1,
    lastTarget: target,
    lastTs: now,
    backoffUntil: 0,
  });
}

export function enterBackoff(cfg: AutoRefreshSetting, now: number): void {
  const state = readState(RELOAD_KEY);
  writeState(RELOAD_KEY, { ...state, backoffUntil: now + cfg.backoffMs });
}

export function getBackoffRemainingMs(now: number): number {
  const { backoffUntil } = readState(RELOAD_KEY);
  return Math.max(0, backoffUntil - now);
}

/**
 * chunk 加载失败的救场闸。页面已经坏了，不判空闲、不等 cap，
 * 但必须限制次数——否则一个持续 404 的 chunk 会造成刷新循环。
 */
export function evaluateChunkReloadPermission(cfg: AutoRefreshSetting, now: number): ReloadGate {
  const state = readState(CHUNK_KEY);
  if (state.attempts >= cfg.chunkMaxAttempts) {
    return { allowed: false, reason: 'chunk-attempts-exhausted', backoff: true };
  }
  if (state.lastTs && now - state.lastTs < cfg.chunkReloadCooldownMs) {
    return { allowed: false, reason: 'chunk-cooldown', backoff: false };
  }
  return { allowed: true, reason: 'ok', backoff: false };
}

export function recordChunkReloadAttempt(now: number): void {
  const state = readState(CHUNK_KEY);
  writeState(CHUNK_KEY, { ...state, attempts: state.attempts + 1, lastTs: now });
}

/** 调试用：清空账本，便于反复验证 */
export function resetLedgers(): void {
  writeState(RELOAD_KEY, { ...EMPTY });
  writeState(CHUNK_KEY, { ...EMPTY });
}
